import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  createReferralCode,
  getLoyaltyHistory,
  getLoyaltyMe,
  LoyaltyHistoryItem,
  LoyaltyMe,
  LoyaltyPhoto,
  redeemPoints,
  RedeemResult,
  ReferralCodeResult,
  uploadLoyaltyPhoto,
} from '../../services/loyalty';
import { formatApiError } from '../../utils/apiError';
import { clearAuthThisTab, getCurrentUser, login, logout, register } from './authSlice';

/**
 * GT Pontok közös állapota: a fejléc-badge és a Fiók pont-panelje ugyanebből
 * dolgozik, így beváltás / feltöltés után mindkettő azonnal frissül.
 * Elavult-válasz védelem: csak a legutóbb indított lekérés eredménye íródik be
 * (requestId / historyRequestId). Az állapot törlődik ki- és bejelentkezéskor,
 * regisztrációkor, a lap kijelentkeztetésekor (másik lapról) és érvénytelen
 * munkamenetnél (getCurrentUser 401) — így másik felhasználó adata sosem marad meg.
 */
export interface LoyaltyState {
  data: LoyaltyMe | null;
  loading: boolean;
  error: string | null;
  requestId: string | null;
  loadingMore: boolean;
  historyRequestId: string | null;
}

const initialState: LoyaltyState = {
  data: null,
  loading: false,
  error: null,
  requestId: null,
  loadingMore: false,
  historyRequestId: null,
};

const LOAD_ERROR = 'Nem sikerült betölteni a GT Pontokat.';

export const fetchLoyalty = createAsyncThunk<LoyaltyMe, void, { rejectValue: string }>(
  'loyalty/fetch',
  async (_, { rejectWithValue }) => {
    try {
      return await getLoyaltyMe();
    } catch {
      return rejectWithValue(LOAD_ERROR);
    }
  },
);

export const loadMoreLoyaltyHistory = createAsyncThunk<
  { results: LoyaltyHistoryItem[]; hasMore: boolean },
  void,
  { state: { loyalty: LoyaltyState }; rejectValue: string }
>('loyalty/loadMoreHistory', async (_, { getState, rejectWithValue }) => {
  const offset = getState().loyalty.data?.history.length ?? 0;
  try {
    return await getLoyaltyHistory(offset);
  } catch (err) {
    return rejectWithValue(formatApiError(err));
  }
});

export const redeemLoyaltyPoints = createAsyncThunk<RedeemResult, number, { rejectValue: string }>(
  'loyalty/redeem',
  async (points, { dispatch, rejectWithValue }) => {
    try {
      const res = await redeemPoints(points);
      dispatch(fetchLoyalty());
      return res;
    } catch (err) {
      return rejectWithValue(formatApiError(err));
    }
  },
);

export const uploadLoyaltyPhotoThunk = createAsyncThunk<
  LoyaltyPhoto,
  { file: File; caption: string },
  { rejectValue: string }
>('loyalty/uploadPhoto', async ({ file, caption }, { dispatch, rejectWithValue }) => {
  try {
    const res = await uploadLoyaltyPhoto(file, caption);
    dispatch(fetchLoyalty());
    return res;
  } catch (err) {
    return rejectWithValue(formatApiError(err));
  }
});

export const requestReferralCode = createAsyncThunk<ReferralCodeResult, void, { rejectValue: string }>(
  'loyalty/referralCode',
  async (_, { rejectWithValue }) => {
    try {
      return await createReferralCode();
    } catch (err) {
      return rejectWithValue(formatApiError(err));
    }
  },
);

const loyaltySlice = createSlice({
  name: 'loyalty',
  initialState,
  reducers: {
    resetLoyalty: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLoyalty.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.requestId = action.meta.requestId;
        // Az új első oldal mellett a folyamatban lévő „Továbbiak" eredménye elavul.
        state.historyRequestId = null;
        state.loadingMore = false;
      })
      .addCase(fetchLoyalty.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return; // elavult válasz
        state.loading = false;
        state.requestId = null;
        state.data = action.payload;
      })
      .addCase(fetchLoyalty.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.loading = false;
        state.requestId = null;
        state.error = action.payload ?? LOAD_ERROR;
      })
      .addCase(loadMoreLoyaltyHistory.pending, (state, action) => {
        state.loadingMore = true;
        state.historyRequestId = action.meta.requestId;
      })
      .addCase(loadMoreLoyaltyHistory.fulfilled, (state, action) => {
        if (state.historyRequestId !== action.meta.requestId) return; // elavult válasz
        state.historyRequestId = null;
        state.loadingMore = false;
        if (!state.data) return;
        const seen = new Set(state.data.history.map((h) => h.id));
        state.data.history.push(...action.payload.results.filter((h) => !seen.has(h.id)));
        state.data.historyHasMore = action.payload.hasMore;
      })
      .addCase(loadMoreLoyaltyHistory.rejected, (state, action) => {
        if (state.historyRequestId !== action.meta.requestId) return;
        state.historyRequestId = null;
        state.loadingMore = false;
        state.error = action.payload ?? LOAD_ERROR;
      })
      .addCase(redeemLoyaltyPoints.fulfilled, (state, action) => {
        // Az egyenleg azonnal frissül (a teljes adat a háttérben töltődik újra).
        if (state.data) {
          state.data.balance = action.payload.balance;
          state.data.coupons = state.data.coupons.map((c) => ({ ...c, available: action.payload.balance >= c.points }));
        }
      })
      .addCase(requestReferralCode.fulfilled, (state, action) => {
        if (state.data) {
          state.data.referral = { ...state.data.referral, code: action.payload.code, url: action.payload.url };
        }
      })
      .addCase(logout.fulfilled, () => initialState)
      .addCase(logout.rejected, () => initialState)
      .addCase(login.fulfilled, () => initialState)
      .addCase(register.fulfilled, () => initialState)
      .addCase(clearAuthThisTab, () => initialState)
      .addCase(getCurrentUser.rejected, (state, action) =>
        (action.payload?.status === 401 ? initialState : state));
  },
});

export const { resetLoyalty } = loyaltySlice.actions;
export default loyaltySlice.reducer;
